import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calculator, CheckCircle, Building2, Percent, Calendar, CreditCard, ArrowRight, Phone, Mail, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Finance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const car = location.state?.car;

  useEffect(() => {
    document.title = 'Vehicle Finance South Africa | Car Loans & Finance Calculator | AutoSearch SA';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Calculate your car finance repayments. Compare vehicle finance from WesBank, Standard Bank, ABSA, FNB, Nedbank & Capitec. Get approved today.');
  }, []);

  const [calculator, setCalculator] = useState({
    price: car?.price || 350000,
    deposit: 0,
    term: 72,
    rate: 11.75, // Current SA prime rate linked vehicle finance
    balloonPercent: 0
  });

  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Finance calculation
  const calculateFinance = () => {
    const principal = calculator.price - calculator.deposit;
    const balloon = (calculator.balloonPercent / 100) * calculator.price;
    const financeAmount = principal - balloon;
    const monthlyRate = calculator.rate / 100 / 12;
    const n = calculator.term;

    if (monthlyRate === 0) {
      return {
        monthly: Math.round(financeAmount / n),
        totalPayment: financeAmount,
        totalInterest: 0,
        balloon
      };
    }

    const monthly = (financeAmount * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    const totalPayment = monthly * n + balloon;
    const totalInterest = totalPayment - principal;

    return {
      monthly: Math.round(monthly),
      totalPayment: Math.round(totalPayment),
      totalInterest: Math.round(totalInterest),
      balloon: Math.round(balloon)
    };
  };

  const finance = calculateFinance();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        name: leadForm.name,
        email: leadForm.email,
        phone: leadForm.phone,
        lead_type: 'finance',
        car_info: car ? { title: car.title, price: car.price, source: car.source } : null,
        message: leadForm.message || `Finance enquiry for R${calculator.price.toLocaleString()} vehicle`
      });

      setSubmitted(true);
    } catch (e) {
      console.error('Lead submission failed:', e);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const banks = [
    { name: 'WesBank', logo: '🚗', rate: 'From 11.25%', url: 'https://www.wesbank.co.za/' },
    { name: 'Standard Bank', logo: '🏦', rate: 'From 11.50%', url: 'https://www.standardbank.co.za/southafrica/personal/products-and-services/borrow-for-your-needs/vehicle-and-asset-finance' },
    { name: 'Nedbank', logo: '🏢', rate: 'From 11.75%', url: 'https://www.nedbank.co.za/content/nedbank/desktop/gt/en/personal/loans/vehicle-finance.html' },
    { name: 'ABSA', logo: '🏛️', rate: 'From 11.75%', url: 'https://www.absa.co.za/personal/borrow/vehicle-finance/' },
    { name: 'FNB', logo: '💳', rate: 'From 12.00%', url: 'https://www.fnb.co.za/loans/vehicle-finance/index.html' },
    { name: 'Capitec', logo: '💰', rate: 'From 12.50%', url: 'https://www.capitecbank.co.za/personal/credit/vehicle-finance/' },
    { name: 'MFC', logo: '🚙', rate: 'From 11.50%', url: 'https://www.mfc.co.za/' },
    { name: 'SA Taxi Finance', logo: '🚐', rate: 'From 14.00%', url: 'https://www.sataxi.co.za/' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-lg">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Your finance enquiry has been submitted. A consultant will contact you within 24 hours.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
        <div className="container">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Vehicle Finance Made Easy</h1>
            <p className="text-xl text-blue-100">
              Get pre-approved for vehicle finance from South Africa's leading banks. 
              Compare rates and find the best deal for your budget.
            </p>
          </div>
        </div>
      </section>

      <div className="container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calculator */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Calculator className="w-6 h-6 text-blue-600" />
                Finance Calculator
              </h2>

              {car && (
                <div className="bg-blue-50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-blue-600 font-medium">Calculating for:</p>
                  <p className="text-lg font-semibold text-gray-900">{car.title}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                    <input
                      type="number"
                      value={calculator.price}
                      onChange={(e) => setCalculator({ ...calculator, price: Number(e.target.value) })}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deposit</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                    <input
                      type="number"
                      value={calculator.deposit}
                      onChange={(e) => setCalculator({ ...calculator, deposit: Number(e.target.value) })}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Term (months)</label>
                  <select
                    value={calculator.term}
                    onChange={(e) => setCalculator({ ...calculator, term: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                  >
                    <option value={24}>24 months</option>
                    <option value={36}>36 months</option>
                    <option value={48}>48 months</option>
                    <option value={54}>54 months</option>
                    <option value={60}>60 months</option>
                    <option value={72}>72 months</option>
                    <option value={84}>84 months</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Interest Rate (%)</label>
                  <input
                    type="number"
                    step="0.25"
                    value={calculator.rate}
                    onChange={(e) => setCalculator({ ...calculator, rate: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Balloon Payment: {calculator.balloonPercent}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="40"
                    value={calculator.balloonPercent}
                    onChange={(e) => setCalculator({ ...calculator, balloonPercent: Number(e.target.value) })}
                    className="w-full"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    A balloon payment reduces monthly instalments but requires a lump sum at the end.
                  </p>
                </div>
              </div>

              {/* Results */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Monthly Payment</p>
                    <p className="text-2xl font-bold text-blue-600">R{finance.monthly.toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Total Interest</p>
                    <p className="text-xl font-semibold text-gray-900">R{finance.totalInterest.toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Total Payment</p>
                    <p className="text-xl font-semibold text-gray-900">R{finance.totalPayment.toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Balloon</p>
                    <p className="text-xl font-semibold text-gray-900">R{finance.balloon.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Banks */}
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Finance Providers</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {banks.map((bank, idx) => (
                  <a 
                    key={idx} 
                    href={bank.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="border border-gray-200 rounded-xl p-4 text-center hover:border-blue-300 hover:bg-blue-50 transition cursor-pointer block"
                  >
                    <div className="text-3xl mb-2">{bank.logo}</div>
                    <h3 className="font-semibold text-gray-900">{bank.name}</h3>
                    <p className="text-sm text-green-600">{bank.rate}</p>
                    <p className="text-xs text-blue-600 mt-2">Visit Website →</p>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Lead Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Get Pre-Approved</h3>
              <p className="text-gray-600 text-sm mb-6">
                Fill in your details and a finance consultant will contact you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      required
                      value={leadForm.name}
                      onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg"
                      placeholder="John Smith"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      required
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      required
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg"
                      placeholder="082 123 4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
                  <textarea
                    value={leadForm.message}
                    onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg resize-none"
                    placeholder="Any additional information..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Get Pre-Approved'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>

              <p className="text-xs text-gray-500 mt-4 text-center">
                By submitting, you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Finance;
